import { useEffect, useRef } from 'react'
import { Copy, Download, FileText, Trash2 } from 'lucide-react'

interface ContextMenuProps {
    x: number
    y: number
    onClose: () => void
    onCopy?: () => void
    onDownload?: () => void
    onDelete?: () => void
    onRaw?: () => void
}

export function ContextMenu({
    x,
    y,
    onClose,
    onCopy,
    onDownload,
    onDelete,
    onRaw
}: ContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [onClose])

    const menuItems = [
        { icon: FileText, label: 'View Raw', action: onRaw, show: !!onRaw },
        { icon: Copy, label: 'Copy Content', action: onCopy, show: !!onCopy },
        { icon: Download, label: 'Download', action: onDownload, show: !!onDownload },
        { icon: Trash2, label: 'Delete', action: onDelete, show: !!onDelete, danger: true }
    ].filter(item => item.show)

    return (
        <div
            ref={menuRef}
            className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1 z-50 min-w-[180px]"
            style={{ left: x, top: y }}
        >
            {menuItems.map((item, index) => (
                <button
                    key={index}
                    onClick={() => {
                        item.action?.()
                        onClose()
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${item.danger
                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                </button>
            ))}
        </div>
    )
}
